import { AlertTriangle, GitMerge, Copy, X, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { eur } from "../lib/format";

/**
 * DuplicateDialog — presented when creating a new product yields a
 * high-confidence match against existing catalog entries.
 * Actions: Fusionner (use existing), Conserver séparément (force create), Annuler.
 * The confidence score is intentionally NOT shown to the user.
 */
export default function DuplicateDialog({ open, candidates, draft, onKeepSeparate, onUseExisting, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-[720px]" data-testid="duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <AlertTriangle className="w-4 h-4 text-[#B36B00]" />
            Ce produit semble déjà exister dans votre catalogue
          </DialogTitle>
        </DialogHeader>

        <p className="text-[13px] text-[#5E6875]">
          Vérifiez si l'un de ces produits correspond à ce que vous voulez ajouter.
          Vous pouvez utiliser l'existant, ou créer un nouveau produit distinct.
        </p>

        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[440px] overflow-y-auto">
          {/* Draft on the left */}
          <div className="rounded-lg border-2 border-[#FF9F1C] bg-[#FFF7E9] p-3" data-testid="duplicate-draft">
            <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#B36B00] mb-1">Nouveau produit</div>
            <div className="font-heading font-extrabold text-[15px] text-[#0B1F33]">{draft?.name || "—"}</div>
            <ProductLine label="Marque" value={draft?.brand || "—"} />
            <ProductLine label="Contenance" value={draft?.content_value && draft?.content_unit ? `${draft.content_value} ${draft.content_unit}` : "—"} />
            <ProductLine label="Catégorie" value={draft?.category || "—"} />
            <ProductLine label="Prix" value={draft?.purchase_price ? `${eur(draft.purchase_price)} / ${draft.purchase_unit}` : "—"} />
            <ProductLine label="Réf. fournisseur" value={draft?.supplier_ref || "—"} />
            <ProductLine label="EAN" value={draft?.ean || "—"} />
          </div>

          {/* Candidates */}
          <div className="space-y-3">
            {(candidates || []).map((c) => (
              <div key={c.id} className="rounded-lg border border-[#CDD7E3] bg-white p-3" data-testid={`duplicate-candidate-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">Existant</div>
                    <div className="font-heading font-extrabold text-[14.5px] text-[#0B1F33] truncate">{c.name}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onUseExisting?.(c)}
                    data-testid={`duplicate-use-${c.id}`}
                    className="!h-8 bg-[#1769E0] hover:bg-[#0B1F33] text-white text-[12px] px-3 gap-1"
                  >
                    <GitMerge className="w-3.5 h-3.5" /> Utiliser celui-ci
                  </Button>
                </div>
                <ProductLine label="Marque" value={c.brand || "—"} />
                <ProductLine label="Contenance" value={c.content_value && c.content_unit ? `${c.content_value} ${c.content_unit}` : "—"} />
                <ProductLine label="Catégorie" value={c.category || "—"} />
                <ProductLine label="Prix" value={c.purchase_price ? `${eur(c.purchase_price)} / ${c.purchase_unit}` : "—"} />
                <ProductLine label="Réf. fournisseur" value={c.supplier_ref || "—"} />
                {(c.reasons || []).length > 0 && (
                  <div className="mt-2 text-[11px] text-[#5E6875] italic">
                    Correspond sur : {c.reasons.join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid="duplicate-cancel"><X className="w-4 h-4" /> Annuler</Button>
          <Button
            onClick={onKeepSeparate}
            data-testid="duplicate-keep-separate"
            className="!h-10 bg-white border-2 border-[#0B1F33] text-[#0B1F33] hover:bg-[#F4F5EF]"
          >
            <Copy className="w-4 h-4" /> Conserver séparément
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductLine({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 mt-1.5 text-[12px]">
      <span className="text-[#5E6875] w-[110px] shrink-0">{label}</span>
      <span className="font-semibold text-[#0B1F33] truncate">{value}</span>
    </div>
  );
}
